import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Grid, Button, ButtonGroup, Box, TextField, Chip, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from '@mui/material';

import { useLazyQuery } from '@apollo/client';
import i18next from 'i18next';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import { debounce } from 'lodash';

import PagedTable from 'components/table/PagedTable';
import { filterSelector, bookingModeSelector } from 'helper/filter';
import { userSelector, hasAnyAdminSpace } from 'helper/security';

import { HOTEL_LIST_QUERY, HOTEL_VALIDATION_QUERY } from '../gql';
import { EHotelListerStatusType, EHotelWidgetStatusType } from '__generated__/graphql';
import { EBookingMode } from 'types';
import { HotelDeleteButton } from './hotel';
import { CountryName } from 'components/Language';

interface ValidationSummary {
  productsWithoutBasePrice: number;
  productsWithoutBasePriceNames: string[];
  productsWithoutPriceComponents: number;
  productsWithoutPriceComponentsNames: string[];
  bundlesWithoutBasePrice: number;
  bundlesWithoutBasePriceNames: string[];
  bundlesWithoutPriceComponents: number;
  bundlesWithoutPriceComponentsNames: string[];
  facilitiesWithoutBasePrice: number;
  facilitiesWithoutBasePriceNames: string[];
  facilitiesWithoutPriceComponents: number;
  facilitiesWithoutPriceComponentsNames: string[];
  // Tax validation
  facilitiesWithoutTaxRates?: number;
  facilitiesWithoutTaxRatesNames?: string[];
  productsWithoutTaxRates?: number;
  productsWithoutTaxRatesNames?: string[];
  bundlesWithoutTaxRates?: number;
  bundlesWithoutTaxRatesNames?: string[];
  serviceTypesIncomplete?: Array<{
    serviceTypeName: string;
    missingProducts: string[];
    missingFacilities: string[];
    invalidProducts: string[];
    invalidFacilities: string[];
  }> | null;
}

const formatValidationSummary = (validation: ValidationSummary | null | undefined): React.ReactNode => {
  if (!validation) return null;

  const parts: React.ReactNode[] = [];
  const total =
    validation.productsWithoutBasePrice +
    validation.productsWithoutPriceComponents +
    validation.bundlesWithoutBasePrice +
    validation.bundlesWithoutPriceComponents +
    validation.facilitiesWithoutBasePrice +
    validation.facilitiesWithoutPriceComponents +
    (validation.facilitiesWithoutTaxRates || 0) +
    (validation.productsWithoutTaxRates || 0) +
    (validation.bundlesWithoutTaxRates || 0);

  if (total === 0 && (!validation.serviceTypesIncomplete || validation.serviceTypesIncomplete.length === 0)) return null;

  if (validation.productsWithoutBasePrice > 0) {
    parts.push(
      <div key="products-base">
        <strong>{i18next.t('pricelist-validation-products-without-base-price')}:</strong>
        <ul style={{ marginTop: '4px', marginBottom: '4px', paddingLeft: '20px' }}>
          {validation.productsWithoutBasePriceNames.map((name, idx) => (
            <li key={idx}>{name}</li>
          ))}
        </ul>
      </div>
    );
  }

  if (validation.productsWithoutPriceComponents > 0) {
    parts.push(
      <div key="products-components">
        <strong>{i18next.t('pricelist-validation-products-without-price-components')}:</strong>
        <ul style={{ marginTop: '4px', marginBottom: '4px', paddingLeft: '20px' }}>
          {validation.productsWithoutPriceComponentsNames.map((name, idx) => (
            <li key={idx}>{name}</li>
          ))}
        </ul>
      </div>
    );
  }

  if (validation.bundlesWithoutBasePrice > 0) {
    parts.push(
      <div key="bundles-base">
        <strong>{i18next.t('pricelist-validation-bundles-without-base-price')}:</strong>
        <ul style={{ marginTop: '4px', marginBottom: '4px', paddingLeft: '20px' }}>
          {validation.bundlesWithoutBasePriceNames.map((name, idx) => (
            <li key={idx}>{name}</li>
          ))}
        </ul>
      </div>
    );
  }

  if (validation.bundlesWithoutPriceComponents > 0) {
    parts.push(
      <div key="bundles-components">
        <strong>{i18next.t('pricelist-validation-bundles-without-price-components')}:</strong>
        <ul style={{ marginTop: '4px', marginBottom: '4px', paddingLeft: '20px' }}>
          {validation.bundlesWithoutPriceComponentsNames.map((name, idx) => (
            <li key={idx}>{name}</li>
          ))}
        </ul>
      </div>
    );
  }

  if (validation.facilitiesWithoutBasePrice > 0) {
    parts.push(
      <div key="facilities-base">
        <strong>{i18next.t('pricelist-validation-facilities-without-base-price')}:</strong>
        <ul style={{ marginTop: '4px', marginBottom: '4px', paddingLeft: '20px' }}>
          {validation.facilitiesWithoutBasePriceNames.map((name, idx) => (
            <li key={idx}>{name}</li>
          ))}
        </ul>
      </div>
    );
  }

  if (validation.facilitiesWithoutPriceComponents > 0) {
    parts.push(
      <div key="facilities-components">
        <strong>{i18next.t('pricelist-validation-facilities-without-price-components')}:</strong>
        <ul style={{ marginTop: '4px', marginBottom: '4px', paddingLeft: '20px' }}>
          {validation.facilitiesWithoutPriceComponentsNames.map((name, idx) => (
            <li key={idx}>{name}</li>
          ))}
        </ul>
      </div>
    );
  }

  // Tax validation - items without tax rates for hotel's country
  if (validation.facilitiesWithoutTaxRates && validation.facilitiesWithoutTaxRates > 0) {
    parts.push(
      <div key="facilities-tax">
        <strong>{i18next.t('validation-facilities-without-tax-rates')}:</strong>
        <ul style={{ marginTop: '4px', marginBottom: '4px', paddingLeft: '20px' }}>
          {(validation.facilitiesWithoutTaxRatesNames || []).map((name, idx) => (
            <li key={idx}>{name}</li>
          ))}
        </ul>
      </div>
    );
  }

  if (validation.productsWithoutTaxRates && validation.productsWithoutTaxRates > 0) {
    parts.push(
      <div key="products-tax">
        <strong>{i18next.t('validation-products-without-tax-rates')}:</strong>
        <ul style={{ marginTop: '4px', marginBottom: '4px', paddingLeft: '20px' }}>
          {(validation.productsWithoutTaxRatesNames || []).map((name, idx) => (
            <li key={idx}>{name}</li>
          ))}
        </ul>
      </div>
    );
  }

  if (validation.bundlesWithoutTaxRates && validation.bundlesWithoutTaxRates > 0) {
    parts.push(
      <div key="bundles-tax">
        <strong>{i18next.t('validation-bundles-without-tax-rates')}:</strong>
        <ul style={{ marginTop: '4px', marginBottom: '4px', paddingLeft: '20px' }}>
          {(validation.bundlesWithoutTaxRatesNames || []).map((name, idx) => (
            <li key={idx}>{name}</li>
          ))}
        </ul>
      </div>
    );
  }

  // Add service type validation details
  if (validation.serviceTypesIncomplete && validation.serviceTypesIncomplete.length > 0) {
    validation.serviceTypesIncomplete.forEach((st, stIdx) => {
      const stDetails: React.ReactNode[] = [];

      if (st.missingProducts.length > 0) {
        stDetails.push(
          <div key={`${stIdx}-missing-products`}>
            {i18next.t('validation-missing-products')}:
            <ul style={{ marginTop: '4px', marginBottom: '4px', paddingLeft: '20px' }}>
              {st.missingProducts.map((name, idx) => (
                <li key={idx}>{name}</li>
              ))}
            </ul>
          </div>
        );
      }

      if (st.missingFacilities.length > 0) {
        stDetails.push(
          <div key={`${stIdx}-missing-facilities`}>
            {i18next.t('validation-missing-services')}:
            <ul style={{ marginTop: '4px', marginBottom: '4px', paddingLeft: '20px' }}>
              {st.missingFacilities.map((name, idx) => (
                <li key={idx}>{name}</li>
              ))}
            </ul>
          </div>
        );
      }

      if (st.invalidProducts.length > 0) {
        stDetails.push(
          <div key={`${stIdx}-invalid-products`}>
            {i18next.t('validation-invalid-products')}:
            <ul style={{ marginTop: '4px', marginBottom: '4px', paddingLeft: '20px' }}>
              {st.invalidProducts.map((name, idx) => (
                <li key={idx}>{name}</li>
              ))}
            </ul>
          </div>
        );
      }

      if (st.invalidFacilities.length > 0) {
        stDetails.push(
          <div key={`${stIdx}-invalid-facilities`}>
            {i18next.t('validation-invalid-services')}:
            <ul style={{ marginTop: '4px', marginBottom: '4px', paddingLeft: '20px' }}>
              {st.invalidFacilities.map((name, idx) => (
                <li key={idx}>{name}</li>
              ))}
            </ul>
          </div>
        );
      }

      if (stDetails.length > 0) {
        parts.push(
          <div key={`servicetype-${stIdx}`}>
            <strong>{st.serviceTypeName}:</strong>
            <div style={{ paddingLeft: '12px' }}>{stDetails}</div>
          </div>
        );
      }
    });
  }

  return parts.length > 0 ? <div style={{ marginTop: '8px' }}>{parts}</div> : null;
};

// Lazy-loading validation details component with dialog
const ValidationDetailsButton: React.FC<{
  hotelId: number;
  hotelName: string;
  type: 'lister' | 'widget';
}> = ({ hotelId, hotelName, type }) => {
  const [open, setOpen] = useState(false);
  const [loadValidation, { data, loading }] = useLazyQuery(HOTEL_VALIDATION_QUERY, {
    fetchPolicy: 'network-only',
  });

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    loadValidation({ variables: { hotelId } });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const hotel = data?.listHotels?.hotels?.[0];
  const validation = type === 'lister' ? hotel?.listerValidation : hotel?.widgetValidation;

  return (
    <>
      <Button
        size="small"
        variant="text"
        onClick={handleOpen}
        sx={{ ml: 0.5, p: 0, minWidth: 'auto' }}
      >
        {i18next.t('details')}
      </Button>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {i18next.t('validation-details-title', { hotelName })}
        </DialogTitle>
        <DialogContent>
          {loading && <CircularProgress />}
          {!loading && !validation && <div>{i18next.t('validation-no-data')}</div>}
          {validation && formatValidationSummary(validation)}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{i18next.t('close')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

/**
 * Hotels list page with pagination and filtering by space and booking mode.
 * Shows hotel details and supports copy/delete actions.
 */
export default function HotelsList() {
  const navigate = useNavigate();
  const filter = filterSelector();
  const user = userSelector()!;
  const bookingMode = bookingModeSelector();

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const [query, { data, loading, error }] = useLazyQuery(HOTEL_LIST_QUERY, {
    // Ensure each page change fetches fresh data, avoiding stale cache results
    fetchPolicy: 'network-only',
  });

  // Debounce search term updates
  const debouncedUpdate = useCallback(
    debounce((value: string) => {
      setDebouncedSearchTerm(value);
    }, 500),
    []
  );

  useEffect(() => {
    debouncedUpdate(searchTerm);
  }, [searchTerm, debouncedUpdate]);

  return (
    <>
      <Helmet>
        <title>{i18next.t('hotels-list-page-title')}</title>
      </Helmet>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2 }}>
            <SearchIcon />
            <TextField
              label={i18next.t('search')}
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                minWidth: 300,
                '& .MuiInputBase-input': { py: 0.5 },
              }}
            />
          </Box>
          <PagedTable
            filterKey={`${JSON.stringify(filter)}-${debouncedSearchTerm}`}
            refetchPage={(skip, take) =>
              query({
                variables: {
                  spaceId: (filter && filter.spaceId) || null,
                  hotelId: (filter && filter.hotelId) || null,
                  skip,
                  take,
                  search: debouncedSearchTerm.length >= 2 ? debouncedSearchTerm : null,
                },
              })
            }
            rowsLoading={loading}
            rowsErr={error}
            totalCount={data?.listHotels.totalCount}
            headers={[
              i18next.t('hotels-list-header-name'),
              i18next.t('hotels-list-header-businesscountry'),
              i18next.t('hotels-list-header-refcode'),
              ...(user.isSingleSpace ? [] : [i18next.t('list-header-space')]),
              ...(bookingMode === EBookingMode.EPILO ? [''] : []),
              ...(bookingMode === EBookingMode.SEM ? ['Status'] : []),
              '',
            ]}
            rows={(data?.listHotels.hotels || []).map(row => [
              <Link to={`/settings/hotels/${row.id}`}>{row.name}</Link>,
              <CountryName country={row.businessCountry} />,
              row.refCode,
              ...(user.isSingleSpace ? [] : [
                <Chip
                  label={row.space.name}
                  onClick={() => navigate(`/settings/spaces/${row.space.id}`)}
                />
              ]),
              ...(bookingMode === EBookingMode.EPILO
                ? [
                  row.listerStatus === EHotelListerStatusType.READY ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label={i18next.t('active')} color="success" size="small" />
                      {row.wordpressUrl && (
                        <Link to={row.wordpressUrl} target="_blank" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          {i18next.t('hotels-list-lister-url')}
                          <OpenInNewIcon fontSize="small" />
                        </Link>
                      )}
                    </Box>
                  ) : row.listerStatus === EHotelListerStatusType.INACTIVE ? (
                    <Chip label={i18next.t('inactive')} color="error" size="small" />
                  ) : (
                    <Alert severity="warning" sx={{ display: 'inline-flex', width: 'auto' }}>
                      {i18next.t(`hotel-lister-status-${row.listerStatus}`)}
                      {row.listerStatus === EHotelListerStatusType.PRICELIST_INCOMPLETE && (
                        <ValidationDetailsButton hotelId={row.id} hotelName={row.name} type="lister" />
                      )}
                    </Alert>
                  ),
                ]
                : []),
              ...(bookingMode === EBookingMode.SEM
                ? [
                  row.widgetStatus === EHotelWidgetStatusType.READY ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label={i18next.t('active')} color="success" size="small" />
                      {row.widgetTestUrl && (
                        <>
                          <Link to={row.widgetTestUrl} target="_blank" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            {i18next.t('hotels-list-test-url')}
                            <OpenInNewIcon fontSize="small" />
                          </Link>{' '}
                          (
                          <Link
                            to={`${row.widgetTestUrl}&seminargoNoRooms=Y`}
                            target="_blank"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                          >
                            {i18next.t('hotels-list-test-url-no-rooms')}
                            <OpenInNewIcon fontSize="small" />
                          </Link>
                          )
                        </>
                      )}
                    </Box>
                  ) : row.widgetStatus === EHotelWidgetStatusType.INACTIVE ? (
                    <Chip label={i18next.t('inactive')} color="error" size="small" />
                  ) : (
                    <Alert severity="warning" sx={{ display: 'inline-flex', width: 'auto' }}>
                      {i18next.t(`hotel-widget-status-${row.widgetStatus}`)}
                      {(row.widgetStatus === EHotelWidgetStatusType.PRICELIST_INCOMPLETE ||
                        row.widgetStatus === EHotelWidgetStatusType.NO_TAX_RATES) && (
                          <ValidationDetailsButton hotelId={row.id} hotelName={row.name} type="widget" />
                        )}
                    </Alert>
                  ),
                ]
                : []),
              <ButtonGroup>
                <HotelDeleteButton id={row.id} spaceId={row.space.id} icon />
              </ButtonGroup>,
            ])}
          />
        </Grid>
        {user.isSeminargo && (
          <Grid item xs={12}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!filter?.spaceId}
              onClick={() => navigate('/settings/hotels/_create')}
            >
              {i18next.t('hotels-list-add')}
            </Button>
          </Grid>
        )}
      </Grid>
    </>
  );
}
